<template>
  <div class="package-cards">
    <article
      v-for="product in products"
      :key="product.uuid"
      class="pricing-card"
      :class="[
        {
          featured: isFeaturedPackage(product),
        },
        `pricing-card--${packageType(product).toLowerCase()}`,
      ]"
      :style="packageCardStyle(product)"
    >
      <div class="pricing-card--top">
        <span class="pricing-card--badge" v-if="isFeaturedPackage(product)"> Rekomendasi </span>

        <h3>{{ offeringName(product) }}</h3>
      </div>

      <div class="pricing-card--price">
        <small v-if="product.discount && product.discount > 0">
          {{ $n(product.price, 'currency', 'id-ID') }}
        </small>

        <strong>{{ packagePriceText(product) }}</strong>

        <span>/ {{ product.membership.duration }} hari</span>
      </div>

      <p class="pricing-card--desc"></p>

      <ul class="pricing-card--features">
        <li v-for="item in packageHighlights(product)" :key="`${product.uuid}-${item}`">
          <v-icon>mdi-check-circle</v-icon>
          <span>{{ item }}</span>
        </li>
      </ul>
    </article>
  </div>
</template>

<script>
export default {
  name: 'package-cards',

  props: {
    products: {
      type: Array,
      default: () => [],
    },
  },

  methods: {
    offeringName(product) {
      if (product && product.name) {
        return product.name.replace('Membership ', '').toUpperCase();
      }

      return '';
    },

    packagePrice(product) {
      if (!product) return 0;

      return product.discount && product.discount > 0
        ? product.price - product.discount
        : product.price;
    },

    packagePriceText(product) {
      if (!product || !product.price) return this.$t('general.free');

      return this.$n(this.packagePrice(product), 'currency', 'id-ID');
    },

    packageDescription(product) {
      const type = this.packageType(product);

      if (type === 'EXECUTIVE') {
        return 'Akses paling lengkap untuk agen yang aktif scale up listing.';
      }

      if (type === 'PRO') {
        return 'Akses profesional untuk listing dan fitur Owner Connect & Project Connect.';
      }

      if (type === 'STARTER') {
        return 'Paket awal untuk mencoba fitur utama BeliRumah Agen.';
      }

      if (type === 'LITE') {
        return 'Paket ringan untuk mulai pasang listing properti.';
      }

      return 'Akses dasar untuk mulai beriklan.';
    },

    packageHighlights(product) {
      if (!product) return [];

      const coinAmount = product.coins && product.coins.amount ? product.coins.amount : 0;
      const listingAmount =
        product.available_listing && product.available_listing.amount
          ? product.available_listing.amount
          : 0;
      const duration =
        product.membership && product.membership.duration ? product.membership.duration : 0;

      const highlights = [
        `${coinAmount} Koin`,
        `${listingAmount} ${this.$t('offering.listingAmount')}`,
        `${duration} Hari masa aktif`,
      ];

      if (this.hasPremiumAccess(product)) {
        highlights.push(this.$t('offering.ownerConnectAccess'));
        highlights.push(this.$t('offering.projectConnectAccess'));
        highlights.push(this.$t('offering.agenNaikKelas'));
      }

      if (this.hasHotLeads(product)) {
        highlights.push(`${this.$t('offering.hotLeads')}*`);
      }

      return highlights;
    },

    packageType(product) {
      const type =
        product && product.membership ? product.membership.type : this.offeringName(product);

      const name = String(type || '')
        .toUpperCase()
        .trim();

      if (name.includes('EXECUTIVE')) return 'EXECUTIVE';
      if (name.includes('STARTER')) return 'STARTER';
      if (name.includes('PRO')) return 'PRO';
      if (name.includes('LITE')) return 'LITE';

      return 'LITE';
    },

    packageAccentColor(product) {
      const fallback = {
        LITE: 'F92E0B',
        STARTER: '5B5C5C',
        PRO: 'F5C04D',
        EXECUTIVE: '3B3A38',
      };

      const type = this.packageType(product);

      const rawColor =
        product && product.membership && product.membership.color_code
          ? product.membership.color_code
          : fallback[type];

      return String(rawColor || fallback.LITE).replace('#', '');
    },

    packageTextColor(product) {
      const color = this.packageAccentColor(product).toUpperCase();

      return color === 'F5C04D' || color === 'FFFFFF' ? '#202124' : '#ffffff';
    },

    packageBorderWidth(product) {
      const type = this.packageType(product);

      return type === 'LITE' ? '3px' : '7px';
    },

    packageCardStyle(product) {
      return {
        '--package-accent': `#${this.packageAccentColor(product)}`,
        '--package-accent-text': this.packageTextColor(product),
        '--package-border-width': this.packageBorderWidth(product),
      };
    },

    isFeaturedPackage(product) {
      const type = this.packageType(product);

      return type === 'PRO';
    },

    hasPremiumAccess(product) {
      const type = this.packageType(product);

      return type === 'PRO' || type === 'EXECUTIVE';
    },

    hasHotLeads(product) {
      const type = this.packageType(product);

      return type === 'EXECUTIVE';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/utils/variables';

.package-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 270px));
  align-items: stretch;
  justify-content: center;
  gap: 20px;
  padding-top: 44px;

  @media only screen and (min-width: 769px) and (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 280px));
    gap: 16px;
  }

  @media #{$phones} {
    grid-template-columns: 1fr;
    padding-top: 14px;
  }
}

.pricing-card {
  --package-accent: #d71920;
  --package-accent-text: #ffffff;
  --package-border-width: 7px;

  position: relative;
  display: flex;
  min-height: 470px;
  flex-direction: column;
  overflow: hidden;
  padding: 28px 24px 26px;
  border: var(--package-border-width) solid var(--package-accent);
  border-radius: 17px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.055), 0 2px 5px rgba(17, 24, 39, 0.04);
  transition: border-color 0.28s cubic-bezier(0.32, 0.72, 0, 1),
    box-shadow 0.28s cubic-bezier(0.32, 0.72, 0, 1), transform 0.28s cubic-bezier(0.32, 0.72, 0, 1);

  &:hover {
    border-color: var(--package-accent);
    transform: translateY(-3px);
    box-shadow: 0 18px 40px rgba(17, 24, 39, 0.1), 0 4px 10px rgba(17, 24, 39, 0.05);
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media only screen and (min-width: 769px) and (max-width: 1100px) {
    min-height: 400px;
    padding: 26px 22px 24px;
  }

  @media #{$phones} {
    min-height: auto;
    padding: 24px 20px 22px;
  }
}

.pricing-card--top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: auto;

  h3 {
    order: 1;
    margin: 0;
    color: var(--package-accent);
    font-family: Poppins-Bold, sans-serif;
    font-size: 25px;
    line-height: 1.25;
    letter-spacing: -0.025em;
  }
}

.pricing-card--badge {
  order: 2;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 30px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  background: var(--package-accent);
  color: var(--package-accent-text);
  font-family: Poppins-Bold, sans-serif;
  font-size: 11px;
  line-height: 1.35;
  text-align: center;
  box-shadow: none;

  @media #{$phones} {
    align-self: flex-start;
    height: auto;
    min-width: 0;
    padding: 7px 12px;
    border: 0;
    border-radius: 999px;
    font-size: 11px;
  }
}

.pricing-card--price {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 7px;
  margin-top: 14px;

  small {
    flex-basis: 100%;
    color: #777f8d;
    font-family: Poppins, sans-serif;
    font-size: 12px;
    text-decoration: line-through;
  }

  strong {
    color: #111318;
    font-family: Poppins-Bold, sans-serif;
    font-size: 33px;
    line-height: 1;
    letter-spacing: -0.035em;
  }

  span {
    color: #6b7280;
    font-family: Poppins-SemiBold, sans-serif;
    font-size: 16px;
  }

  @media only screen and (min-width: 769px) and (max-width: 1100px) {
    strong {
      font-size: 32px;
    }
  }
}

.pricing-card--desc {
  margin: 6px 0 9px;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(24, 27, 33, 0.1);
  color: #333842;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  line-height: 1.65;
}

.pricing-card--features {
  display: grid;
  gap: 14px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr);
    gap: 10px;
    align-items: start;
    color: #181b21;
    font-family: Poppins-SemiBold, sans-serif;
    font-size: $font-sm;
    line-height: 1.45;
  }

  .v-icon {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--package-accent);
    color: var(--package-accent-text);
    font-size: 13px;
  }

  @media only screen and (min-width: 769px) and (max-width: 1100px) {
    gap: 12px;

    li {
      font-size: 12.5px;
    }
  }
}
</style>
